import React, { useState, useEffect } from 'react';
import './MarginConfigForm.css';
import { PageConfigModel } from '../hooks/useAppConfig';

interface MarginConfigFormProps {
  onSave?: (data: PageConfigModel) => void;
  page: PageConfigModel | null; // Optional prop to pass initial config
}

const MarginConfigForm: React.FC<MarginConfigFormProps> = ({ onSave, page}) => {
  const [formData, setFormData] = useState<PageConfigModel | null>(page);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string>('');

  // Actualizar formulario cuando se carga la configuración
  useEffect(() => {
    if (page) {
      setFormData(page);
    }
  }, [page]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    
    if (!formData) return;

    // Crear una copia profunda del formData para evitar mutaciones
    const updatedFormData = { ...formData };

    // Determinar el valor correcto basado en el tipo de input
    const inputValue = type === 'number' ? parseFloat(value) || 0 : value;

    // Manejar las diferentes propiedades según el name
    switch (name) {
      case 'width':
        updatedFormData.width = inputValue as number;
        break;
      case 'height':
        updatedFormData.height = inputValue as number;
        break;
      case 'fontName':
        updatedFormData.font = { ...updatedFormData.font, name: value };
        break;
      case 'fontSize':
        updatedFormData.font = { ...updatedFormData.font, size: inputValue as number };
        break;
      case 'lineSpacing':
        updatedFormData.font = { ...updatedFormData.font, lineSpacing: inputValue as number };
        break;
      case 'marginTop':
        updatedFormData.margins = { ...updatedFormData.margins, top: inputValue as number };
        break;
      case 'marginRight':
        updatedFormData.margins = { ...updatedFormData.margins, right: inputValue as number };
        break;
      case 'marginBottom':
        updatedFormData.margins = { ...updatedFormData.margins, bottom: inputValue as number };
        break;
      case 'marginLeft':
        updatedFormData.margins = { ...updatedFormData.margins, left: inputValue as number };
        break;
      default:
        console.warn(`Campo no reconocido: ${name}`);
        return;
    }

    setFormData(updatedFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    

    try {
      setIsSaving(true);
      setSaveMessage('');

      
      setSaveMessage('Configuración guardada exitosamente');
      if (onSave && formData)
        onSave?.(formData);

      // Limpiar mensaje después de 3 segundos
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      setSaveMessage('Error al guardar la configuración');
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    if(page)
      setFormData(page);
  };


  return (
    <div className="margin-config-form">      
      <form onSubmit={handleSubmit}>
        <div className="margins-section">
          <h3>Hoja General (en centrímetros)</h3>

          <div className="margins-grid">
            <div className="form-group">
              <label htmlFor="height">Alto:</label>
              <input
                type="number"
                id="height"
                name="height"
                value={formData?.height || 0}
                onChange={handleInputChange}
                min="0"
                step="0.01"
              />
            </div>
            <div className="form-group">
              <label htmlFor="width">Ancho:</label>
              <input
                type="number"
                id="width"
                name="width"
                value={formData?.width || 0}
                onChange={handleInputChange}
                min="0"
                step="0.01"
              />
            </div>
            <div className="form-group">
              <label htmlFor="fontName">Fuente:</label>
              <input
                type="text"
                id="fontName"
                name="fontName"
                value={formData?.font.name || ''}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="fontSize">Tamaño Fuente en (ptos):</label>
              <input
                type="number"
                id="fontSize"
                name="fontSize"
                value={formData?.font.size || 0}
                onChange={handleInputChange}
                min="0"
                step="0.01"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="lineSpacing">Interlineado</label>
              <input
                type="number"
                id="lineSpacing"
                name="lineSpacing"
                value={formData?.font.lineSpacing || 0}
                onChange={handleInputChange}
                min="0"
                step="0.01"
              />
            </div>
          </div>

            </div>
        <div className="margins-section">
          <h3>Márgenes (en centrímetros)</h3>
          <div className="margins-grid">
            <div className="form-group">
              <label htmlFor="marginTop">Superior:</label>
              <input
                type="number"
                id="marginTop"
                name="marginTop"
                value={formData?.margins.top || 0}
                onChange={handleInputChange}
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="marginRight">Derecho:</label>
              <input
                type="number"
                id="marginRight"
                name="marginRight"
                value={formData?.margins.right || 0}
                onChange={handleInputChange}
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="marginBottom">Inferior:</label>
              <input
                type="number"
                id="marginBottom"
                name="marginBottom"
                value={formData?.margins.bottom || 0}
                onChange={handleInputChange}
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="marginLeft">Izquierdo:</label>
              <input
                type="number"
                id="marginLeft"
                name="marginLeft"
                value={formData?.margins.left || 0}
                onChange={handleInputChange}
                min="0"
                step="0.01"
              />
            </div>
          </div>
        </div>

        {saveMessage && (
          <div className={`message ${saveMessage.includes('Error') ? 'error' : 'success'}`}>
            {saveMessage}
          </div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            className="btn-primary"
            disabled={isSaving}
          >
            {isSaving ? 'Guardando...' : 'Guardar Configuración'}
          </button>
        </div>
      </form>

    </div>
  );
};

export default MarginConfigForm;
